/**
 * weav3r.dev client — the only source of player-bazaar listings.
 *
 * Torn's own API exposes a bazaar *directory* (busiest, top grossing, and so on)
 * but never per-item listings with prices, so a third-party aggregator is the
 * only way to see what bazaars are actually selling. weav3r needs no key and
 * sends `access-control-allow-origin: *`.
 *
 * It is undocumented and unversioned, so this module is deliberately defensive:
 * every field is coerced, both snake_case and camelCase spellings are accepted,
 * and callers are expected to degrade to market-only if it fails.
 */

import { getJson } from "./http.ts";
import type { ItemDetails, Offer, OfferSource } from "../state/types.ts";
import { toNumber } from "../util/format.ts";

const BASE = "https://weav3r.dev/api";

/** The endpoint's own default and ceiling. */
export const MAX_LISTINGS = 100;

interface MarketplaceBody {
  item_id?: number;
  item_name?: string;
  market_price?: number;
  bazaar_average?: number;
  generated_at?: number;
  listings?: Array<{
    item_id?: number;
    player_id?: number;
    player_name?: string;
    quantity?: number;
    price?: number;
    content_updated?: number;
    last_checked?: number;
  }>;
}

export interface MarketplaceResult {
  itemId: number;
  itemName: string;
  /** Torn's market value as weav3r sees it — we prefer our own catalog value. */
  marketPrice: number | null;
  /** Average bazaar asking price. Useful context Torn does not provide. */
  bazaarAverage: number | null;
  offers: Offer[];
}

/**
 * Bazaar listings for one item, cheapest first, one row per seller.
 *
 * `limit`, `minQty` and `maxPrice` all filter server-side (verified). We still
 * re-filter client-side, as the TornTools userscript does, so the caller's
 * guarantees hold even if the params are ever ignored.
 */
export async function fetchBazaarListings(
  itemId: number,
  opts: {
    signal?: AbortSignal;
    limit?: number;
    minQty?: number;
    maxPrice?: number | null;
  } = {},
): Promise<MarketplaceResult> {
  const params = new URLSearchParams({ limit: String(opts.limit ?? MAX_LISTINGS) });
  if (opts.minQty && opts.minQty > 1) params.set("minQty", String(Math.trunc(opts.minQty)));
  if (opts.maxPrice && opts.maxPrice > 0) params.set("maxPrice", String(Math.trunc(opts.maxPrice)));

  const body = await getJson<MarketplaceBody>(`${BASE}/marketplace/${itemId}?${params}`, {
    signal: opts.signal,
    source: "weav3r",
  });

  const fetchedAt = Date.now();
  const minQty = opts.minQty ?? 0;
  const maxPrice = opts.maxPrice ?? 0;

  const offers: Offer[] = (body.listings ?? [])
    .filter((listing) => {
      const price = toNumber(listing.price);
      if (price === undefined || price <= 0) return false;
      if (typeof listing.player_id !== "number") return false;
      const quantity = toNumber(listing.quantity) ?? 0;
      if (minQty > 1 && quantity > 0 && quantity < minQty) return false;
      if (maxPrice > 0 && price > maxPrice) return false;
      return true;
    })
    .map((listing) => ({
      itemId,
      source: "bazaar" as const,
      price: toNumber(listing.price) as number,
      quantity: toNumber(listing.quantity) ?? 0,
      sellerId: listing.player_id as number,
      sellerName: listing.player_name ?? null,
      details: null,
      lastChecked: toNumber(listing.last_checked) ?? toNumber(listing.content_updated) ?? null,
      fetchedAt,
    }));

  return {
    itemId: toNumber(body.item_id) ?? itemId,
    itemName: body.item_name ?? "",
    marketPrice: toNumber(body.market_price) ?? null,
    bazaarAverage: toNumber(body.bazaar_average) ?? null,
    offers,
  };
}

interface RankedBody {
  total_count?: number;
  weapons?: Array<{
    uid?: string | number;
    itemId?: number;
    item_id?: number;
    itemName?: string;
    weaponType?: string;
    rarity?: string | null;
    damage?: string | number | null;
    accuracy?: string | number | null;
    quality?: string | number | null;
    bonuses?: Record<string, { bonus?: string; value?: string | number; description?: string }>;
    price?: number;
    playerId?: number;
    player_id?: number;
    playerName?: string;
    quantity?: number;
    source?: string;
    lastUpdated?: string;
  }> | null;
}

export type RankedTab = "weapons" | "armor";

/**
 * Ranked (unique) weapons and armour across the item market, bazaars and
 * display cases.
 *
 * `weaponName` / `armorPiece` are exact, case-sensitive item names — "Riot
 * Helmet" matches, "Helmet" returns nothing — so callers must pass a real name
 * from the Torn catalog rather than a user's free text.
 */
export async function fetchRankedListings(
  itemId: number,
  itemName: string,
  tab: RankedTab,
  opts: { signal?: AbortSignal; maxPrice?: number | null } = {},
): Promise<Offer[]> {
  const params = new URLSearchParams({
    tab,
    maxPrice: String(opts.maxPrice && opts.maxPrice > 0 ? Math.trunc(opts.maxPrice) : 1_000_000_000),
    sortField: "price",
    sortDirection: "asc",
  });
  params.set(tab === "weapons" ? "weaponName" : "armorPiece", itemName);

  const body = await getJson<RankedBody>(`${BASE}/ranked-weapons?${params}`, {
    signal: opts.signal,
    source: "weav3r",
  });

  const fetchedAt = Date.now();
  const rows = body.weapons ?? [];
  const offers: Offer[] = [];

  for (const row of rows) {
    const uid = toNumber(row.uid);
    const rowItemId = row.itemId ?? row.item_id;
    const price = toNumber(row.price);
    const sellerId = row.playerId ?? row.player_id ?? null;

    // Guard against the endpoint returning near-matches for a different item.
    if (uid === undefined || rowItemId !== itemId || price === undefined) continue;

    const details: ItemDetails = {
      uid,
      itemId,
      rarity: normaliseRarity(row.rarity),
      damage: toNumber(row.damage),
      accuracy: toNumber(row.accuracy),
      quality: toNumber(row.quality),
      bonuses: Object.values(row.bonuses ?? {})
        .filter((bonus): bonus is { bonus: string; value?: string | number; description?: string } =>
          Boolean(bonus?.bonus),
        )
        .map((bonus) => ({
          name: bonus.bonus,
          value: toNumber(bonus.value) ?? 0,
          description: bonus.description,
        })),
    };

    offers.push({
      itemId,
      source: normaliseSource(row.source),
      price,
      quantity: toNumber(row.quantity) ?? 1,
      sellerId,
      sellerName: row.playerName ?? null,
      details,
      lastChecked: row.lastUpdated ? Math.floor(Date.parse(row.lastUpdated) / 1000) : null,
      fetchedAt,
    });
  }

  return offers;
}

function normaliseRarity(raw: string | null | undefined): ItemDetails["rarity"] {
  if (!raw) return null;
  const lower = raw.toLowerCase();
  return lower === "yellow" || lower === "orange" || lower === "red" ? lower : null;
}

function normaliseSource(raw: string | undefined): OfferSource {
  if (raw === "bazaar") return "bazaar";
  // "display" means a display case — visible, priced, and not for sale.
  if (raw === "display") return "display";
  return "market";
}
